import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Navigate, useLocation } from 'react-router-dom'
import App from './App'
import RoutesEnum from './components/Routes/rootEnum'
import SpinnerBlock from './components/Spinner/SpinnerBlock'
import AcceptInvitationPage from './features/Auth/AcceptInvitationPage'
import AuthCallbackPage from './features/Auth/AuthCallbackPage'
import LoginPage from './features/Auth/LoginPage'
import PersonaGate from './features/Auth/PersonaGate'
import SignedOutPage from './features/Auth/SignedOutPage'
import FederationConsentPage from './features/CommonSettings/FederationProviders/FederationConsentPage'
import ImpersonationBanner from './features/CommonSettings/Impersonation/ImpersonationBanner'

// E8 top-level shell. Renders the unauthenticated LoginPage at /login and the dedicated AuthCallbackPage
// at /auth/callback (the MSAL reply route), and the full authenticated app everywhere else. Rendering the
// callback page here — rather than letting /auth/callback fall through to <App/> — keeps App from firing a
// fresh redirect during/after callback processing, and gives the route a real handler (so it never lands
// on NoMatch). Sits inside MsalProvider (IdentityConfigLoader) and the Router, so every branch has MSAL +
// routing context. E8 F8-1 cutover: /login is now the canonical entry — an unauthenticated visit to any
// protected route is redirected here (App no longer auto-redirects to Entra), preserving the intended path
// as ?returnTo so the post-login callback lands the user back where they started.
const AppShell = () => {
  const { pathname, search } = useLocation()
  const isAuthenticated = useIsAuthenticated()
  const { inProgress } = useMsal()

  if (pathname === RoutesEnum.LOGIN) {
    return <LoginPage />
  }

  if (pathname === RoutesEnum.AUTH_CALLBACK) {
    return <AuthCallbackPage />
  }

  if (pathname === RoutesEnum.SIGNED_OUT) {
    return <SignedOutPage />
  }

  // E10 F10-3: the public invitation acceptance landing. Rendered here (inside MsalProvider, before the
  // auth gate) so the anonymous preview, the prompt=create sign-up, and the post-sign-in authed accept all
  // work on one route. The token is the path segment after /invitation/.
  if (pathname.startsWith('/invitation/')) {
    const token = pathname.slice('/invitation/'.length).split('/')[0]
    return <AcceptInvitationPage token={token} />
  }

  // E20 F20-7 (WI 794): the Microsoft admin-consent return leg — same shape as /invitation/, because the
  // consenting admin may have no LogikSpark session while the AO's own browser must carry the key onward.
  if (pathname.startsWith('/federation-consent/')) {
    const consentStateKey = pathname.slice('/federation-consent/'.length).split('/')[0]
    return <FederationConsentPage consentStateKey={consentStateKey} />
  }

  // Protected routes require an authenticated MSAL account. While an MSAL interaction is still settling
  // (e.g. a redirect result) wait rather than bouncing to /login; once settled and still unauthenticated,
  // route to the explicit login page with the intended path as ?returnTo.
  if (!isAuthenticated) {
    if (inProgress !== InteractionStatus.None) {
      return <SpinnerBlock />
    }
    const returnTo = encodeURIComponent(`${pathname}${search}`)
    return <Navigate to={`${RoutesEnum.LOGIN}?returnTo=${returnTo}`} replace />
  }

  // E8 F8-4: resolve the active persona before the authenticated app makes any scoped API call.
  // E11 R2: the ImpersonationBanner sits inside the gate (so it only mounts once a persona is resolved) and
  // is position:fixed, so its DOM order relative to <App/> doesn't affect layout — it renders null unless an
  // impersonation session is active.
  return (
    <PersonaGate>
      <ImpersonationBanner />
      <App />
    </PersonaGate>
  )
}

export default AppShell
